import * as React from 'react';
import { CellIndex, Timeline, Marker, UtcTime } from 'types';
import { CurrentPosition } from './CurrentPosition';
import { AllMarkers } from './Markers';
import * as moment from 'moment';
import { Spinner } from 'ui/shapes';
import { times } from 'util/times';

import './TimelineView.css';

const oneMinute = 60 * 1000;

const toolTipWidth = 80;
const toolTipHeightWithoutImage = 18;
const toolTipHeightWithImage = 78;

// Position the tooltip so that it doesn't go out of bounds
// (Note: assumes that the timeline window is large enough to display the entire tooltip)
const getTooltipTransform = (hoverX: number, hoverY: number, segmentWidth: number, cellIndex: CellIndex, showImage: boolean) => {
    // Default to +15 pixels to the right unless it doesn't fit... then go left
    const x =
        hoverX + 15 + toolTipWidth > segmentWidth
            ? hoverX - toolTipWidth - 10
            : hoverX + 15;
    
    const toolTipHeight =
        showImage
            ? toolTipHeightWithImage
            : toolTipHeightWithoutImage;

    let maxHeight = 0;
    const element = document.getElementById(`timeline${cellIndex}`);
    if (element) {
        maxHeight = element.scrollTop + element.clientHeight;
    }

    // Default to +5 pixels below unless it doesn't fit... then go up
    const y =
        hoverY + 5 + toolTipHeight > maxHeight
            ? hoverY - toolTipHeight - 5
            : hoverY + 5;
    
    return `translate(${x},${y})`;
};

export type SvgProps = {
    timeline: Timeline;
    tzOffsetCalculator: (utcTime: UtcTime) => number;
    streamPosition: UtcTime;
    playing: boolean;
    onClickMarker: (marker: Marker) => void;
    onMouseOverMarker: (e: React.MouseEvent<SVGElement>, marker: Marker) => void;
    onMouseOutMarker: (e: React.MouseEvent<SVGElement>, marker: Marker) => void;
    onClickTimeline: (time: UtcTime) => void;
    onMouseMove: React.MouseEventHandler<SVGElement>;
    onMouseLeave: React.MouseEventHandler<SVGElement>;
    markerFrameSrc: string;
};

export const TimelineSvg = ({
    cellIndex,
    timeline: {
        baseTime,
        displayWidth,
        displayHeight,
        segmentWidth,
        segmentHeight,
        segmentMargin,
        labelWidth,
        minutesPerSegment,
        scale,
        segments,
        markers: compoundMarkers, // rename to avoid naming collision later on
    },
    segmentImages,
    tzOffsetCalculator,
    streamPosition,
    onClickMarker,
    onClickTimeline,
    onMouseMove,
    onMouseLeave,
    onMouseOverMarker,
    onMouseOutMarker,
    playing,
    styles,
    hoverTime,
    hoverX,
    hoverY,
    markerFrameSrc,
    frameRequestTime,
}: SvgProps & TimelineState & OwnProps & RecomposeProps) =>
    <svg
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        viewBox={ `0 0 ${displayWidth} ${displayHeight}` }
        width={ displayWidth }
        height={ displayHeight }
        onMouseMove={ onMouseMove }
        onMouseLeave={ onMouseLeave }
    >
        <g transform={ `translate(${labelWidth},${segmentMargin})` }>
            { times(segments, i =>
                <g key={ `${segments}-${displayWidth}-${i}` }>
                    { /* Draw the time label to the left */ }
                    <text
                        x="-4"
                        y={  (i + .5) * (segmentHeight + segmentMargin) }
                        textAnchor="end"
                        fontSize="11px"
                        style={ styles.foreground75 }
                    >
                        {
                            moment.utc(baseTime + i * minutesPerSegment * oneMinute)
                                .utcOffset(tzOffsetCalculator(baseTime + i * minutesPerSegment * oneMinute))
                                .format('LT')
                        }
                    </text>
                    { /* Draw the current segment of timeline */ }
                    <image
                        id={ `timeline${cellIndex}image${i}` }
                        xlinkHref={ segmentImages && segmentImages[i] || '' }
                        x={ 0 }
                        y={ i * (segmentHeight + segmentMargin) }
                        width={ segmentWidth }
                        height={ segmentHeight }
                        onClick={ e => {
                            if (!e.currentTarget) return;
                            if (!(e.currentTarget as any).farthestViewportElement) return;
                            const { left } = (e.currentTarget as any).farthestViewportElement.getBoundingClientRect();
                            const milliseconds = (e.clientX - left - labelWidth) / scale;
                            onClickTimeline(baseTime + (i * minutesPerSegment * oneMinute) + milliseconds);
                        } }
                    />
                    { /* Overlay markers */ }
                    <AllMarkers
                        compoundMarkers={ compoundMarkers }
                        segmentHeight={ segmentHeight }
                        segmentWidth={ segmentWidth }
                        segmentMargin={ segmentMargin }
                        i={ i }
                        onClick={ onClickMarker }
                        onMouseOver={ onMouseOverMarker }
                        onMouseOut={ onMouseOutMarker }
                    />
                </g>
            ) }
            { /* Draw the current position indicator */ }
            <CurrentPosition
                baseTime={ baseTime }
                segments={ segments }
                segmentHeight={ segmentHeight }
                segmentWidth={ segmentWidth }
                segmentMargin={ segmentMargin }
                minutesPerSegment={ minutesPerSegment }
                streamPosition={ streamPosition }
                playing={ playing }
            />
            { /* Draw the tooltip */ }
            { hoverTime &&
                <g transform={ getTooltipTransform(hoverX, hoverY, segmentWidth, cellIndex, !!(frameRequestTime || markerFrameSrc)) }>
                    <rect
                        x="0"
                        y="0"
                        width={ toolTipWidth }
                        /* Taller if we're showing an image; shorter if just text */
                        height={ (frameRequestTime || markerFrameSrc) ? toolTipHeightWithImage : toolTipHeightWithoutImage }
                        style={ styles.background80 }
                    />
                    <text
                        x="6"
                        y="13"
                        textAnchor="start"
                        fontSize="11px"
                        style={ styles.foreground75 }
                    >
                        {
                            moment.utc(hoverTime)
                                .utcOffset(tzOffsetCalculator(hoverTime))
                                .format('LTS')
                        }
                    </text>
                    { markerFrameSrc &&
                        <image
                            x="2"
                            y="20"
                            width="76"
                            height="56"
                            xlinkHref={ `data:image/jpeg;base64,${markerFrameSrc}` }
                            preserveAspectRatio="xMidYMid meet"
                        />
                    }
                    { frameRequestTime && !markerFrameSrc &&
                        <g transform="translate(32, 40)">
                            <g className="loading-marker-image">
                                <Spinner
                                    width="16px"
                                    height="16px"
                                    style={ styles.foreground20 }
                                />
                            </g>
                        </g>
                    }
                </g>
            }
        </g>
    </svg>
;

export type TimelineState = {
    tooltipImageUrl: null;
    hoverTime: number | null;
    hoverX: number;
    hoverY: number;
    hoverMarker: Marker | null;
    frameRequestTime: number;
    markerFrameSrc: string;
    segmentImages: string[] | null;
};

export type OwnProps = {
    cellIndex: CellIndex;
};
type RecomposeProps = {
    styles: { [name: string]: React.CSSProperties };
};



// WEBPACK FOOTER //
// ./src/ui/pages/monitoring/mainPanel/timeline/TimelineSvg.tsx